<template>
	<div
		v-if="!props.disabled && hasContent"
		ref="panelRef"
		role="tooltip"
		:class="tooltipClasses"
		:style="tooltipStyles"
		@click.stop
		@mouseenter="emit('hover-change', true)"
		@mouseleave="emit('hover-change', false)"
	>
		<span v-if="props.showArrow" :class="bemm('arrow')"></span>
		<span :class="bemm('content')">
			<slot v-if="$slots.content" name="content"></slot>
			<SafeHtml v-else-if="props.html" tag="span" :html="props.html" />
			<span v-else>{{ props.text }}</span>
		</span>
		<span v-if="hasActions" :class="bemm('actions')">
			<button
				v-for="(action, index) in props.actions"
				:key="action.key ?? `${action.label}-${index}`"
				type="button"
				:class="bemm('action', [action.color ? `color-${action.color}` : ''])"
				:disabled="action.disabled"
				@click="onActionClick(action)"
			>
				{{ action.label }}
			</button>
		</span>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue';
import { useBemm } from 'bemm';
import { SafeHtml } from '../SafeHtml';
import type { Color } from '@arritech/shared/types';
import type {
	TooltipAction,
	TooltipOptions,
	TooltipPlacement,
} from './Tooltip.model';

defineOptions({
	name: 'ArTooltipPanel',
});

const props = withDefaults(
	defineProps<
		TooltipOptions & {
			x?: number;
			y?: number;
			resolvedPlacement?: TooltipPlacement;
			open?: boolean;
		}
	>(),
	{
		text: '',
		html: '',
		color: 'default',
		placement: 'top',
		multiline: false,
		maxWidth: 320,
		showArrow: true,
		actions: () => [],
		disabled: false,
		x: 0,
		y: 0,
		resolvedPlacement: undefined,
		open: true,
	}
);

const emit = defineEmits<{
	(e: 'action-click', action: TooltipAction): void;
	(e: 'hover-change', hovered: boolean): void;
}>();
const slots = useSlots();

const bemm = useBemm('ar-tooltip', {
	return: 'string',
	includeBaseClass: true,
});

const hasActions = computed(
	() => Array.isArray(props.actions) && props.actions.length > 0
);
const isMultiline = computed(
	() => props.multiline || (props.text || '').includes('\n')
);
const hasContent = computed(() =>
	Boolean(props.text || props.html || hasActions.value || slots.content)
);
const placement = computed(() => props.resolvedPlacement || props.placement);

const tooltipClasses = computed(() =>
	bemm('panel', [
		placement.value,
		isMultiline.value ? 'multiline' : 'single',
		props.open ? 'open' : 'closed',
		'fixed',
	])
);

const resolvedColor = computed(() => {
	if (!props.color || props.color === 'default') {
		return {
			background: 'var(--color-foreground)',
			text: 'var(--color-background)',
		};
	}
	const tone = props.color as Color;
	return {
		background: `var(--color-${tone})`,
		text: `var(--color-${tone}-contrast)`,
	};
});

const panelRef = ref<HTMLElement | null>(null);

const tooltipStyles = computed(() => ({
	top: `${props.y}px`,
	left: `${props.x}px`,
	'--tooltip-bg': resolvedColor.value.background,
	'--tooltip-fg': resolvedColor.value.text,
	'--tooltip-max-width':
		typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth,
}));

const onActionClick = (action: TooltipAction) => {
	if (action.disabled) return;
	action.action?.();
	emit('action-click', action);
};

defineExpose({
	panelRef,
});
</script>

<style lang="scss">
.ar-tooltip {
	&__panel {
		position: fixed;
		z-index: 1000;
		background: var(--tooltip-bg);
		color: var(--tooltip-fg);
		border-radius: var(--border-radius);
		padding: var(--space-xs) var(--space-s);
		box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
		max-width: var(--tooltip-max-width);
		width: fit-content;
		text-align: center;
		font-size: var(--font-size-xs);
		line-height: 1.35;
		white-space: nowrap;
		display: inline-flex;
		flex-direction: column;
		gap: var(--space-xs);
		opacity: 0;
		pointer-events: none;
		transition:
			opacity 0.2s var(--bezier),
			transform 0.2s var(--bezier);

		&--open {
			pointer-events: auto;
			animation: showTooltip 0.2s var(--bezier) forwards;
		}

		&--multiline {
			white-space: pre-line;
			word-wrap: break-word;
			width: max-content;
			max-width: min(var(--tooltip-max-width), 90vw);
		}

		@keyframes showTooltip {
			to {
				opacity: 1;
				transform: var(--tooltip-final-transform, translate(-50%, 0));
			}
		}

		&--fixed#{&}--top {
			transform: translate(-50%, 8px);
			--tooltip-final-transform: translate(-50%, 0);
		}

		&--fixed#{&}--bottom {
			transform: translate(-50%, -8px);
			--tooltip-final-transform: translate(-50%, 0);
		}

		&--fixed#{&}--left {
			transform: translate(8px, -50%);
			--tooltip-final-transform: translate(0, -50%);
		}

		&--fixed#{&}--right {
			transform: translate(-8px, -50%);
			--tooltip-final-transform: translate(0, -50%);
		}
	}

	&__arrow {
		position: absolute;
		width: 8px;
		height: 8px;
		background: var(--tooltip-bg);
		transform: rotate(45deg);
	}

	&__panel--top &__arrow {
		left: 50%;
		bottom: -4px;
		transform: translateX(-50%) rotate(45deg);
	}

	&__panel--bottom &__arrow {
		left: 50%;
		top: -4px;
		transform: translateX(-50%) rotate(45deg);
	}

	&__panel--left &__arrow {
		right: -4px;
		top: 50%;
		transform: translateY(-50%) rotate(45deg);
	}

	&__panel--right &__arrow {
		left: -4px;
		top: 50%;
		transform: translateY(-50%) rotate(45deg);
	}

	&__content {
		display: block;
		white-space: wrap;
	}

	&__actions {
		display: inline-flex;
		gap: var(--space-xs);
	}

	&__action {
		border: 0;
		background: color-mix(in srgb, var(--tooltip-fg), transparent 88%);
		color: var(--tooltip-fg);
		padding: calc(var(--space-xs) * 0.5) var(--space-s);
		border-radius: var(--border-radius-s);
		cursor: pointer;
		font-size: var(--font-size-xs);

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}
}
</style>
